// ============================================
// TRANSFERT ET FUSION DE DOSSIERS
// ============================================
// Transfert : plus de demande à accepter. Le dossier est réaffecté
// directement (PUT assignedToId), puis, si une raison est saisie, une note
// interne « Dossier transféré à X : raison » est ajoutée.
// Fusion : le serveur répond 409 quand les deux dossiers appartiennent à des
// clients différents ; l'agent peut alors confirmer.

import { AdminApi, ApiError } from '../../services/api';
import type { Ticket, User } from '../../types';

/** Texte de la note interne ; null sans raison (pas de note). */
export function noteDeTransfert(nomAgent: string, raison: string): string | null {
  const r = raison.trim();
  if (!r) return null;
  return `Dossier transféré à ${nomAgent} : ${r}`;
}

/**
 * Réaffecte le dossier puis ajoute la note. Si la note échoue, la
 * réaffectation est faite : on le signale sans l'annuler.
 */
export async function transfererDossier(
  ticketId: string,
  agent: Pick<User, 'id' | 'displayName' | 'email'>,
  raison: string,
): Promise<{ dossier: Ticket; noteEnregistree: boolean | null }> {
  const dossier = await AdminApi.updateTicket(ticketId, { assignedToId: agent.id });
  const note = noteDeTransfert(agent.displayName || agent.email, raison);
  if (!note) return { dossier, noteEnregistree: null };
  try {
    await AdminApi.sendMessage(ticketId, note, true);
    return { dossier, noteEnregistree: true };
  } catch (e) {
    console.error('Note de transfert non enregistrée :', e);
    return { dossier, noteEnregistree: false };
  }
}

/** Code du 409 qui appelle une confirmation (deux clients différents). */
export const CODE_CLIENTS_DIFFERENTS = 'CLIENTS_DIFFERENTS';

/**
 * Le refus de fusion porte-t-il sur deux clients différents (à confirmer) ?
 * Le serveur répond aussi 409 quand un des dossiers est déjà fusionné : ce
 * refus-là est une erreur simple, aucune confirmation n'y changerait rien.
 */
export function estConflitClientsDifferents(erreur: unknown): erreur is ApiError {
  return erreur instanceof ApiError && erreur.statusCode === 409 && erreur.code === CODE_CLIENTS_DIFFERENTS;
}
